import { ConfirmAlert } from '@/components/Common/ConfirmAlert';
import { ComponentProps, MutableRefObject, useRef, useState } from 'react';

interface IConfirmProps
  extends Omit<
    ComponentProps<typeof ConfirmAlert>,
    'isOpen' | 'onOpenChange' | 'onCancel' | 'onConfirm'
  > {}

interface IDecisionRef {
  props: IConfirmProps;
  promise: Promise<boolean>;
  isOpen: boolean;
  confirm: () => void;
  cancel: () => void;
}

export default function useConfirm() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const decisionRef: MutableRefObject<IDecisionRef | null> = useRef(null);

  const triggerConfirm = async ({ ...rest }: IConfirmProps) => {
    let confirm = () => {};
    let cancel = () => {};
    decisionRef.current = {
      promise: new Promise<boolean>((res) => {
        confirm = () => res(true);
        cancel = () => res(false);
      }),
      confirm,
      cancel,
      isOpen,
      props: rest,
    };

    setIsOpen(true);

    return decisionRef?.current?.promise;
  };

  const alertProps: ComponentProps<typeof ConfirmAlert> = {
    isOpen,
    onOpenChange: () => {
      setIsOpen(false);
    },
    onConfirm: () => {
      decisionRef.current?.confirm();
    },
    onCancel: () => {
      decisionRef.current?.cancel();
    },
    ...decisionRef.current?.props,
  };

  const Alert = () => <ConfirmAlert {...alertProps} />;

  return {
    triggerConfirm,
    decisionRef,
    alertProps,
    Alert,
    isOpen,
    setIsOpen,
  };
}
